import UndoIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/Undo.js';
import { FilterToolbar, PageHeader } from '@diligentcorp/atlas-theme-mui-lens';
import { Box, Button } from '@mui/material';
import React, { ComponentProps, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Redirect, useLocation } from 'react-router-dom';
import { ActionMeta } from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { EmptyState, HeroMetricCard } from 'src/components/atlas/molecules';
import {
  RowModel,
  SpeakUpTable,
} from 'src/components/atlas/organisms/SpeakUpTable/SpeakUpTable';
import emptyStateImage from 'src/components/CategoryConfiguration/assets/atlas-empty-state.svg';
import { CaseDataDownloadButton } from 'src/components/molecules';
import { CaseCreateNewButton } from 'src/components/molecules/CaseCreateNewButton/CaseCreateNewButton';
import { AppliedFiltersType } from 'src/graphql';
import {
  SearchValueProps,
  useCalculateTableRows,
  useCaseSearchQueryParams,
  useIsFeatureEnabled,
  usePageQueryParams,
  useRedirect,
} from 'src/hooks';
import { AtlasMuiThemeProvider } from 'src/theme/atlas/atlasMuiThemeProvider';
import * as Types from '../../../types';
import { FeatureFlag } from '../../../types';
import { useCaseMetrics } from './hooks/useCaseMetrics';
import { PageContentBox } from './SpeakUpCases.styles';
import { useCaseTable } from './useCaseTable';

const PADDING = 24;
const ACTIONS_ROW = 40;
const MARGIN = 20;
const TABLE_HEADER = 56;
const ROW_HEIGHT = 56;
const MIN_ROWS = 7;
const PAGINATION_PADDING = 6;
const PAGE_SPACE_EXCLUDING_ROWS =
  2 * PADDING + ACTIONS_ROW + TABLE_HEADER + MARGIN + 2 * PAGINATION_PADDING;

export const SpeakUpCases: React.FC = () => {
  const { showRedirect, redirectTo, redirect } = useRedirect();
  const caseDataDownloadFeatureFlagEnabled = useIsFeatureEnabled(
    FeatureFlag.CASE_DATA_DOWNLOAD,
  );
  const caseLinkingEnabled = useIsFeatureEnabled(FeatureFlag.LINKING_CASES);

  const { page, setPage, resetParams } = usePageQueryParams('/cases');
  const { rows } = useCalculateTableRows(
    ROW_HEIGHT,
    MIN_ROWS,
    PAGE_SPACE_EXCLUDING_ROWS,
  );

  const [numPages, setNumPages] = useState(page ?? 1);
  const [appliedFilters, setAppliedFilters] = useState<AppliedFiltersType>({});

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const {
    caseQueryParamFields,
    paramSearchValues,
    setCaseQueryParamFields,
    updateUrlParams,
  } = useCaseSearchQueryParams(queryParams);

  const {
    columns,
    sortableColumns,
    tableData,
    isLoading,
    isError,
    caseSearchOptions,
    searchCases,
    addNewReport,
    addExistingReport,
    sortColumns,
    openReport,
    sortedColumn,
    count,
  } = useCaseTable(
    rows,
    page,
    redirect,
    appliedFilters,
    caseQueryParamFields,
    caseLinkingEnabled,
  );

  const [searchValue, setSearchValue] =
    useState<ComponentProps<typeof CreatableSelect>['value']>();
  const [activeCard, setActiveCard] = useState<string | null>(null);

  useEffect(() => {
    if (paramSearchValues && paramSearchValues.length > 0) {
      setSearchValue(paramSearchValues);
    }
  }, [paramSearchValues]);

  const currentUser = useSelector(
    (state: Types.State) => state.globalUsers.currentUser,
  );
  const hasListAllCases =
    currentUser?.systemPermissions?.includes('LIST_ALL_CASES');
  const caseDataDownloadEnabled =
    hasListAllCases && caseDataDownloadFeatureFlagEnabled;

  // Reset page number on error
  useEffect(() => {
    if (isError) {
      setPage(1);
    }
  }, [setPage, isError]);

  // Update number of pages when table data or rows change
  useEffect(() => {
    if (count) {
      setNumPages(Math.ceil(count / rows));
    }
  }, [count, rows]);

  // Set page to highest page number if greater than max
  useEffect(() => {
    if (Number(page) > numPages) {
      setPage(numPages);
    }
  }, [numPages, page, setPage]);

  const CARDS: { id: string; label: string; tooltip: string }[] = [
    {
      id: 'caseLead',
      label: 'Cases assigned to you as a case lead',
      tooltip: 'Cases where you are the case lead',
    },
    {
      id: 'teamMember',
      label: 'Cases assigned to you as a team member',
      tooltip: 'Cases where you are a team member',
    },
    {
      id: 'waitingForResponse',
      label: 'Waiting for response',
      tooltip: 'New cases pending review',
    },
  ];

  const onCardClick = (id: string) => {
    if (id === activeCard) {
      setActiveCard(null);
      setAppliedFilters({});
      setCaseQueryParamFields(null);
      return;
    }
    setActiveCard(id);
    setPage(1);
    if (id === 'caseLead') {
      setAppliedFilters({ caseLead: true, teamMember: false });
      setCaseQueryParamFields(null);
    } else if (id === 'teamMember') {
      setAppliedFilters({ caseLead: false, teamMember: true });
      setCaseQueryParamFields(null);
    } else {
      setAppliedFilters({ caseLead: false, teamMember: false });
      setCaseQueryParamFields([
        { fieldName: 'Case.status', fieldValue: 'new' },
      ]);
    }
  };

  const handleOnSearchChange = (
    newValue: unknown,
    actionMeta: ActionMeta<unknown>,
  ) => {
    const selectedOption = newValue as SearchValueProps;
    searchCases?.(selectedOption);
    setSearchValue(selectedOption);
    updateUrlParams?.(selectedOption);

    if (
      (actionMeta.action === 'clear' || actionMeta.action === 'pop-value') &&
      setCaseQueryParamFields &&
      resetParams
    ) {
      setCaseQueryParamFields(null);
      resetParams();
    }
  };

  const onReset = () => {
    setSearchValue('');
    setActiveCard(null);
    setAppliedFilters({});
    setCaseQueryParamFields(null);
    searchCases?.([]);
    resetParams?.();
  };

  const metrics = useCaseMetrics();

  return (
    <AtlasMuiThemeProvider>
      <PageContentBox
        className="routing-body-content"
        data-testid="speak-up-table-page"
      >
        {showRedirect && (
          <Redirect data-testid={redirectTo} to={`${redirectTo}`} push />
        )}
        <div>
          <PageHeader
            pageTitle="Speak Up"
            pageSubtitle="Access and manage speak up cases"
            moreButton={
              <Box sx={{ display: 'flex', gap: 2 }}>
                {caseDataDownloadEnabled && <CaseDataDownloadButton />}
                <CaseCreateNewButton
                  addNewReport={addNewReport}
                  addExistingReport={addExistingReport}
                />
              </Box>
            }
          />
        </div>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '16px',
          }}
        >
          {CARDS.map((card) => {
            return (
              <HeroMetricCard
                key={card.id}
                data-testid={`hero-metric-card-${card.id}`}
                label={card.label}
                infoTooltip={card.tooltip}
                count={metrics[card.id as keyof typeof useCaseMetrics] ?? 0}
                isLoading={metrics.isLoading}
                selected={activeCard === card.id}
                onClick={() => onCardClick(card.id)}
              />
            );
          })}
        </Box>
        <Box>
          <FilterToolbar
            search={
              <Box
                sx={{
                  minWidth: '250px',
                  maxWidth: '700px',
                }}
              >
                <CreatableSelect
                  placeholder="Search"
                  options={caseSearchOptions}
                  value={searchValue}
                  onChange={handleOnSearchChange}
                  isMulti
                  isClearable
                />
              </Box>
            }
            buttons={
              <Button
                sx={{
                  minWidth: '80px',
                }}
                variant="text"
                data-testid="disclosures-toolbar-reset"
                onClick={onReset}
              >
                <UndoIcon />
                Reset
              </Button>
            }
          />
        </Box>
        <SpeakUpTable
          data-testid="cases-table"
          caseLinkingEnabled={caseLinkingEnabled}
          sortableColumns={sortableColumns}
          sortedColumn={sortedColumn}
          sortColumns={sortColumns}
          columns={columns(caseLinkingEnabled)}
          rows={tableData as { tableRowData: RowModel }[]}
          onPageChange={(index: number) => setPage(index)}
          isLoading={isLoading}
          isError={isError}
          page={Number(page)}
          totalPages={numPages}
          onRowClick={openReport}
          emptyState={
            <EmptyState
              illustration={
                <img
                  src={emptyStateImage}
                  alt="No cases"
                  width="160"
                  height="132"
                />
              }
              title={searchValue ? 'No results found' : 'No cases available'}
              description={
                searchValue
                  ? 'Review your filters or refine your search to view more results.'
                  : 'You have currently no cases in the system.'
              }
            />
          }
        />
      </PageContentBox>
    </AtlasMuiThemeProvider>
  );
};
